import ReactFlow, {
  Background,
  ReactFlowProvider,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import { nodeTypes } from "../../Graph/GraphNodes";
import { BaseContext } from "../../../../../../../contexts/BaseContext";
import { GraphContext } from "../../../../../../../contexts/GraphContext";
import { useContext, useEffect, useRef, useLayoutEffect } from "react";
import { tagService } from "../../../../../../../services/api";
import { processData } from "../../GraphUtils";
import { processTagTagData } from "../../Graph/TagTagUtils";
import { TagTagEdge } from "../../Graph/TagTagEdge";
import {
  checkNodeOverlap,
  removeNodeFromStructure,
  FitViewOnNodesChange,
  canMoveNodeToTarget,
} from "./Utils";
import { toast } from "react-hot-toast";

const ExtractMetaGraph = ({
  // Graph data
  nodes,
  edges,

  // Graph state setters
  setNodes,
  setEdges,

  // Graph event handlers
  onNodesChange,
  onEdgesChange,
  handleAddNodeApplied,

  // Selection state
  setSelectedClassifyFiles,

  // View control
  shouldFitView,
}) => {
  const { fitView } = useReactFlow();
  const renderCountRef = useRef(0);
  // Context data
  const { savedTags, deasyUserConfig } = useContext(BaseContext);
  const {
    setGraphs,
    selectedNodeData,
    setSelectedNodeData,
    setSidePanelOpen,
    hierarchyStats,
    discoveryGraphData,
    updateDiscoveryGraphData,
    selectedDiscoveryGraph,
    activeDragNode,
    activeOverlappingNode,
    setActiveDragNode,
    setActiveOverlappingNode,
    setSelectedNodes,
    handleSaveGraph,
    setShouldFitView,
    setIsViewCentered,
    focusedNodeId,
    showNodesWithNoValue,
    graphLayoutDirection,
    graphViewMode,
    setFocusedNodeId,
  } = useContext(GraphContext);

  const { deasyApiKey } = deasyUserConfig;

  useLayoutEffect(() => {
    renderCountRef.current += 1;
  });

  // Edge types for custom edge rendering
  const edgeTypes = {
    smoothstep: TagTagEdge,
    step: TagTagEdge,
  };

  // Helper function to process graph data based on view mode
  const processGraphData = (
    data,
    stats = null,
    showNodes = true,
    layout = "vertical",
  ) => {
    if (graphViewMode === "tag-value") {
      return processData(data, stats, showNodes, layout);
    } else {
      return processTagTagData(data, stats, layout, selectedDiscoveryGraph);
    }
  };

  // Simple fitView when shouldFitView is true (general fitView)
  useEffect(() => {
    if (shouldFitView && nodes.length > 0) {
      // Try to find the root node
      const rootNode = nodes.find(
        (node) =>
          node.type === "root" ||
          node.data?.label === "Root" ||
          node.id === "root",
      );

      if (rootNode) {
        fitView({
          nodes: [rootNode],
          duration: 500,
          padding: 1,
          maxZoom: 1.5,
        });
      } else {
        fitView({
          padding: 0.3,
          includeHiddenNodes: false,
          duration: 500,
        });
      }
      // Reset shouldFitView to false after a short delay to allow animation to complete
      // and prevent re-triggering.
      const timer = setTimeout(() => {
        setShouldFitView(false);
        setIsViewCentered(true); // Assuming this flag indicates the view has been centered
      }, 500); // 300ms delay, adjust as needed

      return () => clearTimeout(timer); // Cleanup timer on unmount or re-run
    }
  }, [shouldFitView, fitView, setShouldFitView, setIsViewCentered, nodes]);

  // Effect to focus on a specific node when focusedNodeId changes (for search)
  useEffect(() => {
    if (focusedNodeId && nodes.length > 0) {
      // Try to find node by matching the path-based ID to the node's path
      const nodeToFocus = nodes.find((node) => {
        if (!node.data?.nodePath || node.data.nodePath.length === 0) {
          return false;
        }
        // Generate the same path-based ID format used in tagNodes
        const pathId = node.data.nodePath
          .join("_")
          .replace(/\s+/g, "_")
          .toLowerCase();
        return pathId === focusedNodeId;
      });

      if (nodeToFocus) {
        fitView({
          nodes: [nodeToFocus],
          duration: 600,
          maxZoom: 1.5, // Zoom in a bit more on the focused node
        });
        // Clear the focused node ID after focusing to allow re-focusing on the same node
        setTimeout(() => {
          setFocusedNodeId(null);
        }, 700);
      } else {
        console.warn(`Node with focusedNodeId "${focusedNodeId}" not found`);
      }
    }
  }, [focusedNodeId, nodes, fitView, setFocusedNodeId]);

  // Effect to handle view mode changes
  useEffect(() => {
    if (discoveryGraphData && Object.keys(discoveryGraphData).length > 0) {
      const { nodes: updatedNodes, edges: updatedEdges } = processGraphData(
        discoveryGraphData,
        hierarchyStats,
        showNodesWithNoValue,
        graphLayoutDirection,
      );

      setNodes(updatedNodes);
      setEdges(updatedEdges);

      // Remove automatic view setting when switching modes
      // Let the user control when to center the view
      // setShouldFitView(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    graphViewMode,
    discoveryGraphData,
    hierarchyStats,
    showNodesWithNoValue,
    graphLayoutDirection,
  ]);

  // Graph Functions
  const handleNodeDeletion = (nId) => {
    // Get all descendant nodes recursively
    const nodesToDelete = new Set();

    const collectDescendants = (currentId) => {
      nodesToDelete.add(currentId);
      const childEdges = edges.filter((edge) => edge.source === currentId);
      childEdges.forEach((edge) => collectDescendants(edge.target));
    };

    collectDescendants(nId);

    // Update the discoveryGraphData by removing nodes
    const updatedData = JSON.parse(JSON.stringify(discoveryGraphData));

    nodesToDelete.forEach((nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (node) {
        removeNodeFromStructure(updatedData, node.data.nodePath);
      }
    });

    // Re-process the graph
    const { nodes: updatedNodes, edges: updatedEdges } = processGraphData(
      updatedData,
      hierarchyStats,
      showNodesWithNoValue,
      graphLayoutDirection,
    );

    updateDiscoveryGraphData(updatedData);
    setGraphs((prev) =>
      prev.map((graph) =>
        graph.graph_id === selectedDiscoveryGraph.graph_id
          ? { ...graph, graph_data: updatedData }
          : graph,
      ),
    );
    setNodes(updatedNodes);
    setEdges(updatedEdges);
    handleSaveGraph({ ...selectedDiscoveryGraph, graph_data: updatedData });
  };

  const handleMoveTagNode = (sourceNodeData, targetNodeData) => {
    const isRootTarget =
      targetNodeData.type === "root" ||
      targetNodeData.label === "Root" ||
      !targetNodeData.nodePath ||
      targetNodeData.nodePath.length === 0;

    if (
      !isRootTarget &&
      !canMoveNodeToTarget(sourceNodeData.nodePath, targetNodeData.nodePath)
    ) {
      toast.error("Cannot move the node to this location");
      return;
    }

    const updatedData = JSON.parse(JSON.stringify(discoveryGraphData));

    let sourceNode = updatedData;
    const sourcePath = sourceNodeData.nodePath;

    if (!sourcePath || sourcePath.length === 0) {
      toast.error("Cannot move this node - invalid source path");
      return;
    }

    for (let i = 0; i < sourcePath.length - 1; i++) {
      sourceNode = sourceNode[sourcePath[i]];

      if (!sourceNode) {
        toast.error("Cannot move this node - path navigation failed");
        return;
      }
    }

    const nodeKey = sourcePath[sourcePath.length - 1];
    const extractedNode = { [nodeKey]: sourceNode[nodeKey] };

    delete sourceNode[nodeKey];

    if (isRootTarget) {
      Object.assign(updatedData, extractedNode);
    } else {
      let targetNode = updatedData;
      const targetPath = targetNodeData.nodePath || [];

      try {
        for (const pathItem of targetPath) {
          targetNode = targetNode[pathItem];
          if (!targetNode) {
            throw new Error(`Invalid path segment: ${pathItem}`);
          }
        }

        Object.assign(targetNode, extractedNode);
      } catch (error) {
        console.error("Path navigation error:", error);
        toast.error("Cannot move the node - target location is invalid");
        return;
      }
    }

    const { nodes: updatedNodes, edges: updatedEdges } = processGraphData(
      updatedData,
      hierarchyStats,
      showNodesWithNoValue,
      graphLayoutDirection,
    );

    updateDiscoveryGraphData(updatedData);
    setGraphs((prev) =>
      prev.map((graph) =>
        graph.graph_id === selectedDiscoveryGraph.graph_id
          ? { ...graph, graph_data: updatedData }
          : graph,
      ),
    );
    setNodes(updatedNodes);
    setEdges(updatedEdges);

    handleSaveGraph({ ...selectedDiscoveryGraph, graph_data: updatedData });

    // Don't auto-fit view when moving nodes to preserve user's current position

    toast.success(
      `Successfully moved "${nodeKey}" to ${isRootTarget ? "root level" : "new location"}`,
    );
  };

  const handleNodeAddClicked = (nodeData, nodeType) => {
    setSelectedNodeData({ ...nodeData, nodeType });
    setSidePanelOpen(true);
    handleSaveGraph({
      ...selectedDiscoveryGraph,
      graph_data: discoveryGraphData,
    });
  };

  const handleAddNodeToValue = (tagData, selectedValue) => {
    // Create a value node path for the selected value
    const valueNodeData = {
      ...tagData,
      label: selectedValue,
      nodePath: [...tagData.nodePath, selectedValue],
      nodeType: "value",
    };

    setSelectedNodeData(valueNodeData);
    setSidePanelOpen(true);
    handleSaveGraph({
      ...selectedDiscoveryGraph,
      graph_data: discoveryGraphData,
    });
  };

  const handleAddAvailableValue = async (selectedNodeData, newValue) => {
    // Create a deep copy of the data structure
    const updatedData = JSON.parse(JSON.stringify(discoveryGraphData));
    let current = updatedData;

    // Navigate to the tag node
    for (let i = 0; i < selectedNodeData.nodePath.length; i++) {
      const pathItem = selectedNodeData.nodePath[i];
      current = current[pathItem];
    }

    // Initialize TagAvailableValues array if it doesn't exist
    if (!current.TagAvailableValues) {
      current.TagAvailableValues = [];
    }

    // Handle both single value and array of values
    const valuesToAdd = Array.isArray(newValue) ? newValue : [newValue];

    // Filter out any "<ANY>" value and add new values if they don't already exist
    current.TagAvailableValues = current.TagAvailableValues.filter(
      (value) => value !== "<ANY>",
    );

    // Add all new values that don't already exist
    valuesToAdd.forEach((value) => {
      if (!current.TagAvailableValues.includes(value)) {
        current.TagAvailableValues.push(value);
      }
    });

    // Save the updated tag
    const completeTag = savedTags.find(
      (t) => t.name === selectedNodeData.label,
    );
    completeTag.available_values = Array.from(
      new Set([
        ...(completeTag.available_values || []),
        ...current.TagAvailableValues,
      ]),
    );
    await tagService.updateTag(completeTag, deasyApiKey);

    if ("<ANY>" in current) {
      delete current["<ANY>"];
    }

    // Add value nodes for each new value if they don't exist
    valuesToAdd.forEach((value) => {
      if (!current[value]) {
        current[value] = {};
      }
    });

    // Update the graph
    const { nodes: updatedNodes, edges: updatedEdges } = processGraphData(
      updatedData,
      hierarchyStats,
      showNodesWithNoValue,
      graphLayoutDirection,
    );

    updateDiscoveryGraphData(updatedData);
    setGraphs((prev) =>
      prev.map((graph) =>
        graph.graph_id === selectedDiscoveryGraph.graph_id
          ? { ...graph, graph_data: updatedData }
          : graph,
      ),
    );
    setNodes(updatedNodes);
    setEdges(updatedEdges);

    // Don't auto-fit view when adding values to preserve user's current position
  };

  const handleRemoveAvailableValue = async (availableValueToRemove) => {
    // Create a deep copy of the data structure
    const updatedData = JSON.parse(JSON.stringify(discoveryGraphData));
    let current = updatedData;

    // Navigate to the tag node
    const tagPath = availableValueToRemove.nodePath.slice(0, -1); // Remove the value itself
    for (const pathItem of tagPath) {
      current = current[pathItem];
    }

    // Remove the value from TagAvailableValues array
    if (current.TagAvailableValues) {
      current.TagAvailableValues = current.TagAvailableValues.filter(
        (value) => value !== availableValueToRemove.label,
      );
    }

    // Remove the value node and its subtree
    delete current[availableValueToRemove.label];

    // If TagAvailableValues is now empty, add the "<ANY>" node
    if (
      !current.TagAvailableValues ||
      current.TagAvailableValues.length === 0
    ) {
      current["<ANY>"] = {};
    }

    // Update the graph
    const { nodes: updatedNodes, edges: updatedEdges } = processGraphData(
      updatedData,
      hierarchyStats,
      showNodesWithNoValue,
      graphLayoutDirection,
    );

    updateDiscoveryGraphData(updatedData);
    setGraphs((prev) =>
      prev.map((graph) =>
        graph.graph_id === selectedDiscoveryGraph.graph_id
          ? { ...graph, graph_data: updatedData }
          : graph,
      ),
    );
    setNodes(updatedNodes);
    setEdges(updatedEdges);

    // Don't auto-fit view when removing values to preserve user's current position
  };

  const handleClearValues = async (tagData) => {
    // Create a deep copy of the data structure
    const updatedData = JSON.parse(JSON.stringify(discoveryGraphData));
    let current = updatedData;

    // Navigate to the tag node
    for (const pathItem of tagData.nodePath) {
      if (!current[pathItem]) {
        toast.error("Error accessing tag node");
        return;
      }
      current = current[pathItem];
    }

    // Get all current value nodes (excluding TagAvailableValues)
    const valueNodes = Object.keys(current).filter(
      (key) => key !== "TagAvailableValues",
    );

    // Remove all value nodes and their subtrees
    for (const valueNode of valueNodes) {
      delete current[valueNode];
    }

    // Clear the TagAvailableValues array
    current.TagAvailableValues = [];

    // Add the "<ANY>" node for open-ended tag
    current["<ANY>"] = {};

    // Update the graph
    const { nodes: updatedNodes, edges: updatedEdges } = processGraphData(
      updatedData,
      hierarchyStats,
      showNodesWithNoValue,
      graphLayoutDirection,
    );

    updateDiscoveryGraphData(updatedData);
    setGraphs((prev) =>
      prev.map((graph) =>
        graph.graph_id === selectedDiscoveryGraph.graph_id
          ? { ...graph, graph_data: updatedData }
          : graph,
      ),
    );
    setNodes(updatedNodes);
    setEdges(updatedEdges);

    // Don't auto-fit view when clearing values to preserve user's current position

    toast.success(`Cleared all values for "${tagData.label}" tag`);
  };

  const getConnectedValueNodes = (tagNodeId) => {
    return edges
      .filter((edge) => edge.source === tagNodeId)
      .map((edge) => edge.target);
  };

  const getAllDescendantNodes = (startNodeId) => {
    const descendants = new Set();

    const collectDescendants = (nodeId) => {
      const childEdges = edges.filter((edge) => edge.source === nodeId);

      childEdges.forEach((edge) => {
        const childId = edge.target;
        if (!descendants.has(childId)) {
          descendants.add(childId);
          collectDescendants(childId); // Recursive call
        }
      });
    };

    collectDescendants(startNodeId);
    return Array.from(descendants);
  };

  const handleToggleSubtreeVisibility = (tagNodeId, isCollapsed) => {
    const valueNodeIds = getConnectedValueNodes(tagNodeId);

    const allDescendantIds = new Set();
    valueNodeIds.forEach((valueNodeId) => {
      allDescendantIds.add(valueNodeId);
      const descendants = getAllDescendantNodes(valueNodeId);
      descendants.forEach((id) => allDescendantIds.add(id));
    });

    const nodesToHide = new Set([...allDescendantIds]);

    const updatedNodes = nodes.map((node) => {
      if (nodesToHide.has(node.id)) {
        return { ...node, hidden: isCollapsed };
      }
      return node;
    });

    const updatedEdges = edges.map((edge) => {
      const sourceHidden = nodesToHide.has(edge.source);
      const targetHidden = nodesToHide.has(edge.target);

      const isTagEdge =
        edge.source === tagNodeId && nodesToHide.has(edge.target);

      return {
        ...edge,
        hidden: isCollapsed && (sourceHidden || targetHidden || isTagEdge),
      };
    });

    setNodes(updatedNodes);
    setEdges(updatedEdges);

    Array.from(nodesToHide).forEach((nodeId) => {
      const nodeElement = document.querySelector(`[data-id="${nodeId}"]`);
      if (nodeElement) {
        nodeElement.style.display = isCollapsed ? "none" : "";
      }
    });

    document.querySelectorAll(".react-flow__edge").forEach((edge) => {
      const sourceId = edge.getAttribute("data-source");
      const targetId = edge.getAttribute("data-target");
      const shouldHide =
        nodesToHide.has(sourceId) ||
        nodesToHide.has(targetId) ||
        (sourceId === tagNodeId && nodesToHide.has(targetId));

      edge.style.display = isCollapsed && shouldHide ? "none" : "";
    });
  };

  const nodesWithCallbacks = nodes.map((node) => ({
    ...node,
    data: {
      ...node.data,
      onDeleteNode: handleNodeDeletion,
      onAddNode: handleNodeAddClicked,
      onAddNodeToValue: handleAddNodeToValue,
      handleAddNodeApplied: handleAddNodeApplied,
      onAddAvailableValue: handleAddAvailableValue,
      onRemoveAvailableValue: handleRemoveAvailableValue,
      onClearValues: handleClearValues,
      getConnectedValueNodes: getConnectedValueNodes,
      getAllDescendantNodes: getAllDescendantNodes,
      onToggleSubtreeVisibility: handleToggleSubtreeVisibility,
      onMoveTagNode: handleMoveTagNode,
    },
  }));

  // Drag and drop functions
  const handleNodeDrag = (_, node) => {
    setActiveDragNode(node.id);
    setActiveOverlappingNode(checkNodeOverlap(node, nodesWithCallbacks));
  };
  const resetDragNode = () => {
    if (!activeOverlappingNode) return;

    try {
      const activeNodeData = nodesWithCallbacks.find(
        (node) => node.id === activeDragNode,
      )?.data;

      const overlappingNodeData = nodesWithCallbacks.find(
        (node) => node.id === activeOverlappingNode,
      )?.data;

      if (!activeNodeData || !overlappingNodeData) {
        console.error("Could not find active or overlapping node data");
        return;
      }

      const isTargetRoot =
        overlappingNodeData.label === "Root" ||
        !overlappingNodeData.nodePath ||
        overlappingNodeData.nodePath.length === 0;

      if (isTargetRoot) {
        handleMoveTagNode(activeNodeData, overlappingNodeData);
      } else {
        const activeNodePath = activeNodeData.nodePath;
        const overlappingNodePath = overlappingNodeData.nodePath;

        const updatedData = JSON.parse(JSON.stringify(discoveryGraphData));

        let sourceParent = updatedData;
        for (let i = 0; i < activeNodePath.length - 1; i++) {
          if (!sourceParent[activeNodePath[i]]) {
            throw new Error("Invalid source path navigation");
          }
          sourceParent = sourceParent[activeNodePath[i]];
        }

        const nodeKey = activeNodePath[activeNodePath.length - 1];
        const nodeContent = sourceParent[nodeKey];

        if (!nodeContent) {
          throw new Error("Source node not found at path");
        }

        let targetParent = updatedData;
        for (let i = 0; i < overlappingNodePath.length - 1; i++) {
          if (!targetParent[overlappingNodePath[i]]) {
            throw new Error("Invalid target path navigation");
          }
          targetParent = targetParent[overlappingNodePath[i]];
        }

        const targetKey = overlappingNodePath[overlappingNodePath.length - 1];

        if (!targetParent[targetKey]) {
          targetParent[targetKey] = {};
        }

        targetParent[targetKey][nodeKey] = nodeContent;

        delete sourceParent[nodeKey];

        updateDiscoveryGraphData(updatedData);

        const { nodes: updatedNodes, edges: updatedEdges } = processGraphData(
          updatedData,
          hierarchyStats,
          showNodesWithNoValue,
          graphLayoutDirection,
        );

        setNodes(updatedNodes);
        setEdges(updatedEdges);

        handleSaveGraph({
          ...selectedDiscoveryGraph,
          graph_data: updatedData,
        });
      }
    } catch (error) {
      console.error("Error in resetDragNode:", error);
    } finally {
      setActiveDragNode(null);
      setActiveOverlappingNode(null);
    }
  };

  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const onDrop = (event) => {
    event.preventDefault();

    let tag;
    try {
      const draggedItem = event.dataTransfer.getData("draggedItem");
      tag = draggedItem ? JSON.parse(draggedItem) : null;
    } catch (e) {
      console.error("Error parsing dragged item:", e);
      return;
    }

    if (!tag) return;

    // Set the selected node data for the root if no node is selected
    if (!selectedNodeData) {
      setSelectedNodeData({
        label: "Root",
        nodeType: "root",
        nodePath: [],
      });
    }

    setTimeout(() => {
      handleAddNodeApplied(selectedNodeData, tag);
    }, 0);
  };

  // Node click functions
  const handleNodeClick = (event, node) => {
    // Check if it's a root node
    if (node.data.nodeType === "root") {
      // Select all files
      setSelectedClassifyFiles(null); // null indicates all files are selected
      // Clear any selected nodes since we're selecting all files
      setSelectedNodes([]);
    } else {
      // Regular node selection logic
      setSelectedNodes((prev) => {
        const isSelected = prev.some((n) => n.id === node.id);
        // Clear file selection when selecting nodes
        if (!isSelected) {
          setSelectedClassifyFiles([]);
        }
        return isSelected
          ? prev.filter((n) => n.id !== node.id)
          : [...prev, node];
      });
    }
  };

  const onPaneClick = () => {
    setSidePanelOpen(false);
    setSelectedNodeData(null);
  };

  return (
    <div className="relative w-full h-full">
      <ReactFlow
        nodes={nodesWithCallbacks}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitViewOnInit={false}
        onNodeDrag={handleNodeDrag}
        onNodeDragStop={resetDragNode}
        onDragOver={onDragOver}
        onDrop={onDrop}
        minZoom={0.1}
        maxZoom={4}
        defaultEdgeOptions={{
          type: "step",
          style: {
            stroke: "#d1d5db",
            opacity: 1, // Explicitly set opacity to 1
            strokeOpacity: 1, // Also set stroke opacity explicitly
          },
          animated: false,
        }}
        proOptions={{
          hideAttribution: true,
        }}
        nodesDraggable={true}
        elementsSelectable={true}
        animateNodes={true}
        edgesFocusable={false}
        autoPanOnNodeDrag={false}
        smoothEdges
        useStrictMode={false}
        onNodeClick={handleNodeClick}
        onPaneClick={onPaneClick}
      >
        <FitViewOnNodesChange
          nodes={nodes}
          edges={edges}
          shouldFitView={shouldFitView}
          setShouldFitView={setShouldFitView}
        />
        <Background />
      </ReactFlow>
    </div>
  );
};

export const ExtractMetaGraphPage = (props) => {
  return (
    <ReactFlowProvider>
      <ExtractMetaGraph {...props} />
    </ReactFlowProvider>
  );
};
